import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Save, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  DiscordEmbedPreview,
  EmbedEditor,
  embedToForm,
  emptyForm,
  formToEmbed,
  type DiscordEmbed,
  type EmbedFormState,
} from '@raynex/discord-ui';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DiscordChannelMultiPicker, DiscordRolePicker } from '@/components/discord/DiscordPickers';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import {
  useAnnouncements,
  usePublishAnnouncementNow,
  useScheduleAnnouncement,
  useUnpublishAnnouncement,
  useUnscheduleAnnouncement,
  useUpdateAnnouncement,
} from '@/lib/queries';
import { useCan } from '@/lib/use-can';

type MentionKind = 'none' | '@everyone' | '@here' | 'role';

function parseMention(value: string | null): { kind: MentionKind; roleId: string } {
  if (!value) return { kind: 'none', roleId: '' };
  if (value === '@everyone' || value === '@here') return { kind: value, roleId: '' };
  if (value.startsWith('role:')) return { kind: 'role', roleId: value.slice(5) };
  return { kind: 'none', roleId: '' };
}

function buildMention(kind: MentionKind, roleId: string): string | null {
  if (kind === 'none') return null;
  if (kind === '@everyone' || kind === '@here') return kind;
  if (kind === 'role') return roleId ? `role:${roleId}` : null;
  return null;
}

export function AnnouncementDetailPage() {
  const can = useCan();
  const { id = '' } = useParams();
  const list = useAnnouncements();
  const update = useUpdateAnnouncement();
  const publishNow = usePublishAnnouncementNow();
  const scheduleM = useScheduleAnnouncement();
  const unschedule = useUnscheduleAnnouncement();
  const unpublishM = useUnpublishAnnouncement();
  const ann = list.data?.find((a) => a.id === id);

  const [name, setName] = useState('');
  const [channelIds, setChannelIds] = useState<string[]>([]);
  const [mentionKind, setMentionKind] = useState<MentionKind>('none');
  const [mentionRoleId, setMentionRoleId] = useState('');
  const [embedForm, setEmbedForm] = useState<EmbedFormState>(emptyForm());
  const [scheduledAt, setScheduledAt] = useState('');

  useEffect(() => {
    if (!ann) return;
    setName(ann.name);
    setChannelIds(ann.targetChannelIds);
    const m = parseMention(ann.mention);
    setMentionKind(m.kind);
    setMentionRoleId(m.roleId);
    setEmbedForm(embedToForm(ann.embed as DiscordEmbed));
    setScheduledAt(ann.scheduledAt ?? '');
  }, [ann]);

  const previewEmbed = useMemo(() => formToEmbed(embedForm), [embedForm]);

  if (!can('announcements.manage')) {
    return <p className="text-muted-foreground">You don't have access to this page.</p>;
  }
  if (list.isLoading) return <p className="text-muted-foreground">loading…</p>;
  if (!ann) {
    return (
      <Button variant="ghost" size="sm" asChild>
        <Link to="/announcements">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to list
        </Link>
      </Button>
    );
  }

  // Saves current form state to the DB. Used both by the Save button and
  // automatically before Publish Now / Schedule so the bot reads the latest
  // content. `silent` mode is used by auto-save (no success toast).
  async function saveCurrent(silent = false): Promise<boolean> {
    if (channelIds.length === 0) {
      toast.error('Pick at least one channel');
      return false;
    }
    try {
      await update.mutateAsync({
        id,
        patch: {
          name,
          embed: formToEmbed(embedForm) as Record<string, unknown>,
          targetChannelIds: channelIds,
          mention: buildMention(mentionKind, mentionRoleId),
        },
      });
      if (!silent) toast.success('Saved');
      return true;
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
      return false;
    }
  }

  async function handleSave() {
    await saveCurrent(false);
  }

  async function handlePublishNow() {
    if (!confirm('Publish current draft to all target channels right now?')) return;
    // Auto-save first so the bot publishes the latest content, not the version
    // that was last manually saved.
    const saved = await saveCurrent(true);
    if (!saved) return;
    try {
      await publishNow.mutateAsync(id);
      toast.success('Publish queued');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleSchedule() {
    if (!scheduledAt) {
      toast.error('Pick a date/time first');
      return;
    }
    const when = new Date(scheduledAt);
    if (!Number.isFinite(when.getTime()) || when.getTime() <= Date.now()) {
      toast.error('scheduledAt must be a valid future ISO datetime');
      return;
    }
    // Auto-save first so the scheduled publish picks up the latest content.
    const saved = await saveCurrent(true);
    if (!saved) return;
    try {
      await scheduleM.mutateAsync({ id, scheduledAt: when.toISOString() });
      toast.success('Scheduled');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleUnschedule() {
    try {
      await unschedule.mutateAsync(id);
      toast.success('Unscheduled');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleUnpublish() {
    if (!confirm('Delete the posted messages from Discord?')) return;
    try {
      await unpublishM.mutateAsync(id);
      toast.success('Unpublish queued');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/announcements">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to list
            </Link>
          </Button>
          <h1 className="mt-2 flex items-center gap-2 text-3xl font-bold tracking-tight">
            {name}
            <Badge variant={ann.status === 'sent' ? 'default' : 'secondary'}>{ann.status}</Badge>
          </h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} disabled={update.isPending}>
            <Save className="mr-2 h-4 w-4" />
            Save draft
          </Button>
          <Button onClick={handlePublishNow} disabled={publishNow.isPending || update.isPending}>
            <Send className="mr-2 h-4 w-4" />
            Publish now
          </Button>
          {(ann.publishedMessages.length > 0 || ann.status === 'sent') && (
            <Button variant="outline" onClick={handleUnpublish} disabled={unpublishM.isPending}>
              <X className="mr-2 h-4 w-4" />
              Unpublish
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name (internal label)</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Target channels</Label>
            <DiscordChannelMultiPicker value={channelIds} onChange={setChannelIds} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="mentionKind">Mention</Label>
              <Select value={mentionKind} onValueChange={(v) => setMentionKind(v as MentionKind)}>
                <SelectTrigger id="mentionKind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No mention</SelectItem>
                  <SelectItem value="@everyone">@everyone</SelectItem>
                  <SelectItem value="@here">@here</SelectItem>
                  <SelectItem value="role">Role (by ID)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mentionKind === 'role' && (
              <div className="space-y-2">
                <Label>Role to mention</Label>
                <DiscordRolePicker value={mentionRoleId} onValueChange={setMentionRoleId} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Embed</CardTitle>
          <CardDescription>What gets posted to each channel</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <EmbedEditor value={embedForm} onChange={setEmbedForm} />
            <div className="sticky top-4 self-start">
              <DiscordEmbedPreview embed={previewEmbed} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Schedule</CardTitle>
          <CardDescription>
            {ann.status === 'scheduled' && ann.scheduledAt
              ? `Scheduled for ${new Date(ann.scheduledAt).toLocaleString()}`
              : 'Pick a future ISO datetime to schedule auto-publication'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="scheduledAt">Scheduled at (ISO datetime, local time)</Label>
            <Input
              id="scheduledAt"
              type="datetime-local"
              value={scheduledAt.replace(/Z$/, '').slice(0, 16)}
              onChange={(e) => setScheduledAt(e.target.value)}
              disabled={ann.status === 'sent'}
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSchedule} disabled={scheduleM.isPending}>
              <Calendar className="mr-2 h-4 w-4" />
              Schedule
            </Button>
            {ann.status === 'scheduled' && (
              <Button variant="outline" onClick={handleUnschedule} disabled={unschedule.isPending}>
                Cancel schedule
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
